/**
 * DropdownMenu — контекстное меню (Radix). Наш стиль: arrow-const + ref-проп.
 */

import type {ComponentPropsWithoutRef, ComponentRef, Ref} from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import {Check} from 'lucide-react';

import {cn} from '@lib/utils';

export const DropdownMenu = DropdownMenuPrimitive.Root;
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
export const DropdownMenuGroup = DropdownMenuPrimitive.Group;

type ContentProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.Content>>;
};
export const DropdownMenuContent = ({className, sideOffset = 4, ref, ...props}: ContentProps) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
);

type ItemProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean;
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.Item>>;
};
export const DropdownMenuItem = ({className, inset, ref, ...props}: ItemProps) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-4',
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
);

type CheckboxItemProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.CheckboxItem>>;
};
export const DropdownMenuCheckboxItem = ({
  className,
  children,
  checked,
  ref,
  ...props
}: CheckboxItemProps) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      'relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex size-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Check className="size-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
);

type LabelProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.Label>>;
};
export const DropdownMenuLabel = ({className, ref, ...props}: LabelProps) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-sm font-semibold', className)}
    {...props}
  />
);

type SeparatorProps = ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator> & {
  ref?: Ref<ComponentRef<typeof DropdownMenuPrimitive.Separator>>;
};
export const DropdownMenuSeparator = ({className, ref, ...props}: SeparatorProps) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
);
